import { GraphQLFieldConfig, GraphQLNonNull } from 'graphql'
import {
  GenericMutationResponseType,
  GenericMutationResponse
} from '../types/generic-mutation-response'
import { Context } from '../shared'
import { Server, serverType } from '../types/server'
import {
  ServerSettingsMutationArgs,
  serverSettingsMutationArgs,
  BulkServerSettingsFilterMutationArgs,
  bulkServerSettingsFilterMutationArg,
  BulkServerSettingsMutationArgs,
  bulkServerSettingsMutationArg,
  BulkServerSettings
} from '../types/server-settings-args'
import { findByIDAndOrganizationID } from '../queries/helpers/server'
import { mapServerFilters } from '../endpoint-helpers/servers'

export const updateServerSettings: GraphQLFieldConfig<
  any,
  Context,
  ServerSettingsMutationArgs
> = {
  description: "Update a server's settings",
  type: new GraphQLNonNull(serverType),
  args: serverSettingsMutationArgs,
  resolve: async (_source, args, context: Context): Promise<Server> => {
    const { input } = args

    if (!input) {
      throw new Error('mutation input parameter is required')
    }

    const {
      id,
      organizationID,
      settings: { name, logPath, environment, logLevel, assess, protect }
    } = input

    const samplingFields = assess?.sampling
      ? {
          sampling: true,
          sampling_baseline: assess.sampling.baseline,
          sampling_frequency: assess.sampling.frequency,
          sampling_window: assess.sampling.window,
          sampling_profile: assess?.samplingProfile
        }
      : {
          sampling: false,
          sampling_profile: assess?.samplingProfile
        }

    // backend requires sampling/profile be mutually exclusive
    if (assess?.samplingProfile) {
      // @ts-ignore this will go away in time,
      delete samplingFields.sampling
    }

    const assessFields = assess
      ? {
          assess: true,
          stacktrace_capture_mode: assess.stacktraceCaptureMode,
          ...samplingFields
        }
      : { assess: false }

    const syslogFields =
      protect && protect.syslog
        ? {
            syslog_enabled: true,
            syslog_ip_address: protect.syslog.ipAddress,
            syslog_port_number: protect.syslog.port,
            syslog_facility_code: protect.syslog.facilityCode,
            syslog_protocol: protect.syslog.protocol,
            syslog_severity_exploited: protect.syslog.severityExploited,
            syslog_severity_suspicious: protect.syslog.severitySuspicious,
            syslog_severity_blocked: protect.syslog.severityBlocked,
            syslog_severity_blocked_perimeter:
              protect.syslog.severityBlockedPerimeter,
            syslog_severity_probed: protect.syslog.severityProbed
          }
        : { syslog_enabled: false }

    const protectFields = protect
      ? {
          defend: true,
          bot_protection: protect.botProtection,
          ...syslogFields
        }
      : { defend: false }

    const { connector } = context

    await Promise.all([
      connector.put(`/${organizationID}/servers/${id}/edit`, {
        name,
        environment,
        log_level: logLevel,
        log_path: logPath,
        ...assessFields
      }),
      connector.put(
        `/${organizationID}/servers/${id}/edit/defense`,
        protectFields
      )
    ])

    return findByIDAndOrganizationID(id, organizationID, connector)
  }
}

export const updateServerSettingsByIds: GraphQLFieldConfig<
  any,
  Context,
  BulkServerSettingsMutationArgs
> = {
  description: 'Update a server settings by a list of server IDs',
  type: new GraphQLNonNull(GenericMutationResponseType),
  args: bulkServerSettingsMutationArg,
  resolve: async (
    _source,
    args,
    context: Context
  ): Promise<GenericMutationResponse> => {
    const { input } = args

    if (!input) {
      throw new Error('mutation input parameter is required')
    }

    const { ids, organizationID, settings } = input

    const { connector } = context

    await connector.put(`/${organizationID}/servers/bulk/settings`, {
      serverIds: ids,
      settings: mapBulkRequestSettings(settings)
    })

    return { _placeholder: true }
  }
}

export const updateServerSettingsByFilters: GraphQLFieldConfig<
  any,
  Context,
  BulkServerSettingsFilterMutationArgs
> = {
  description: 'Update a server settings by filters',
  type: new GraphQLNonNull(GenericMutationResponseType),
  args: bulkServerSettingsFilterMutationArg,
  resolve: async (
    _source,
    args,
    context: Context
  ): Promise<GenericMutationResponse> => {
    const { input } = args

    if (!input) {
      throw new Error('mutation input parameter is required')
    }

    const { filters, organizationID, settings } = input

    const { connector } = context

    await connector.put(`/${organizationID}/servers/bulk/settings/filters`, {
      filters: mapServerFilters(filters),
      settings: mapBulkRequestSettings(settings)
    })

    return { _placeholder: true }
  }
}

export function mapBulkRequestSettings(settings: BulkServerSettings) {
  // alter key case for backend
  const { assess } = settings
  // @ts-ignore ts bug? samplingProfile does exist on Partial<AssetSettings>
  if (assess) {
    const { samplingProfile, ...rest } = assess
    settings.assess = samplingProfile
      ? {
          //@ts-ignore
          sampling_profile: samplingProfile,
          ...rest
        }
      : assess
  }

  if (settings.protect && settings.protect.syslog) {
    return {
      ...settings,
      protect: {
        ...settings.protect,
        syslog: {
          ...settings.protect.syslog,
          // TODO Remove probed perimeter prop after TS-2505 is complete.
          // This is necessary because we have removed this property from the UI form
          // but it is still required in the backend validator when defend is true.
          connectionType: 'UNENCRYPTED',
          severityProbedPerimeter: 'WARNING'
        }
      }
    }
  }

  return settings
}
